import type { AxiosError } from "axios";
import jwt_decode from "jwt-decode";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLocalStorage } from "react-use";
import styled from "styled-components";
import type { AppUser } from "../context/auth-context";
import { useAuth } from "../context/auth-context";
import { AnimatedLabelInput } from "./AnimatedLabelInput";

import type { ErrorResponse } from "src/lib/types";
import {
	LOCAL_STORAGE_KEYS,
	MEMBER_ROUTE,
	device,
	isValidEmailRegex,
} from "../lib/constants";
import { PrimaryButtonWithoutIcon } from "./Buttons";

const Form = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
`;

export const ErrorText = styled.p`
  font-size: 12px;
  font-family: 'ProximaNova', sans-serif;
  color: ${({ theme }) => theme.colors.secondary.red};
`;

const LoginButton = styled(PrimaryButtonWithoutIcon)`
  width: 100%;
`;

const ButtonContainer = styled.div`
  margin-top: 40px;
  @media ${device.laptop} {
    width: 100%;
  }
`;

interface IFormData {
	email: string;
	password: string;
}

export const LoginForm = ({ className }: { className?: string }) => {
	const { useSignin, setUserRole, useGetUserRole, setAppUser } = useAuth();
	const { t } = useTranslation();
	const { mutate: getUserRole } = useGetUserRole();
	const [, saveToLocalStorage] = useLocalStorage<AppUser>(
		LOCAL_STORAGE_KEYS.USER_DATA,
		null,
	);
	const [, saveTokenToLocalStorage] = useLocalStorage<string>(
		LOCAL_STORAGE_KEYS.TOKEN,
		null,
	);
	const [, saveRefreshTokenToLocalStorage] = useLocalStorage<string>(
		LOCAL_STORAGE_KEYS.REFRESH_TOKEN,
		null,
	);
	const navigate = useNavigate();
	const {
		control,
		handleSubmit,
		formState: { errors, isValid, isSubmitting },
	} = useForm({
		mode: "all",
	});
	const { mutate: signin, isLoading: signinRequestInProgress } = useSignin();
  

	const handleFormSubmit = (data: IFormData) => {
		signin(
			{
				email: data.email,
				password: data.password,
			},
			{
				onSuccess: async (userDataToken) => {
					const decodedUser = jwt_decode(userDataToken.token) as AppUser;

					setAppUser(decodedUser);
					getUserRole(null, {
						onSuccess: async (userRole) => {
							saveToLocalStorage(decodedUser);
							saveTokenToLocalStorage(userDataToken.token);
							saveRefreshTokenToLocalStorage(userDataToken.refreshToken);
							navigate(MEMBER_ROUTE.GET_STARTED);
							setUserRole(userRole); 
						},
						onError: (error: AxiosError<ErrorResponse>) => {
							const backendError = error?.response?.data?.error_description;
							toast.error(
								backendError || error?.response?.data?.message || error.message,
							);
						},
					});
				},
				onError: (error: AxiosError<ErrorResponse>) => {
					const backendError = error?.response?.data?.error_description;
					toast.error(
						backendError || error?.response?.data?.message || error.message,
					);
				},
			},
		);
	};
	const disabled = !isValid || isSubmitting || signinRequestInProgress;

	return (
		<Form className={className} onSubmit={handleSubmit(handleFormSubmit)}>
			<div className="border-0 border-b border-borderColor mt-5 flex flex-row">
				<Controller
					control={control}
					rules={{
						required: t("login.email.required"),
						pattern: {
							value: isValidEmailRegex,
							message: t("login.email.invalid"),
						},
					}}
					name="email"
					render={({ field: { onBlur, onChange, value, ref } }) => (
						<AnimatedLabelInput
							label={t("login.email")}
							name="email"
							type="email"
							value={value}
              ref={ref}
							onChange={onChange}
							onBlur={onBlur}
							isLabelAnimated={false}
						/>
					)}
				/>
			</div>
			{errors?.email?.message && <ErrorText>{errors.email.message}</ErrorText>}
			<div className="border-0 border-b border-borderColor mt-5 flex flex-row">
				<Controller
					control={control}
					name="password"
					rules={{
						required: t("login.password.required"),
					}}
					render={({ field: { ref, onChange, value, onBlur } }) => (
						<AnimatedLabelInput
							label={t("login.password")}
							isLabelAnimated={false}
							isPassword
							value={value}
							name="password"
							type="password"
							onChange={onChange}
							onBlur={onBlur}
							ref={ref}
						/>
					)}
				/>
			</div>

			{errors?.password?.message && (
				<ErrorText>{errors.password.message}</ErrorText>
			)}

			<ButtonContainer>
				<LoginButton
					title={t("login.title")}
					disabled={disabled}
					loading={signinRequestInProgress}
				/>
			</ButtonContainer>
		</Form>
	);
};
